import React, { createContext, FC, ReactNode, useContext, useMemo } from 'react';

/**
 * All user-facing strings rendered by the table chrome. The component ships
 * only English defaults; translated content is the consuming app's concern —
 * supply it via `TanstackTableLocaleProvider` (typically wired to the app's own
 * i18n) or a per-table `localeText` prop.
 */
export interface TanstackTableLocale {
  /** Floating label for the search field. Unset by default. */
  searchLabel?: string;
  searchPlaceholder: string;
  /** Floating label for the free-text column filter. Unset by default. */
  filterLabel?: string;
  columnsButton: string;
  toggleAll: string;
  filter: string;
  clearFilter: string;
  pinLeft: string;
  pinRight: string;
  unpin: string;
  groupByColumn: string;
  ungroup: string;
  filterAll: string;
  noValues: string;
  min: string;
  max: string;
  equals: string;
  from: string;
  to: string;
  yes: string;
  no: string;
  filterPlaceholder: string;
  densityCompact: string;
  densityStandard: string;
  densityComfortable: string;
  /**
   * Label of the `autoPageSize` entry. `{rows}` is replaced with the fitted row
   * count. Single braces on purpose: i18next only claims `{{…}}`, so this
   * survives being routed through an app's translation files untouched.
   */
  autoPageSizeLabel: string;
}

/** English fallback used when nothing is provided higher in the tree. */
export const defaultTanstackTableLocale: TanstackTableLocale = {
  searchPlaceholder: 'Search…',
  columnsButton: 'Columns',
  toggleAll: 'Toggle all',
  filter: 'Filter',
  clearFilter: 'Clear filter',
  pinLeft: 'Pin left',
  pinRight: 'Pin right',
  unpin: 'Unpin',
  groupByColumn: 'Group by this column',
  ungroup: 'Ungroup',
  filterAll: 'All',
  noValues: 'No values',
  min: 'Min',
  max: 'Max',
  equals: 'Equals',
  from: 'From',
  to: 'To',
  yes: 'Yes',
  no: 'No',
  filterPlaceholder: 'Filter…',
  densityCompact: 'Compact',
  densityStandard: 'Standard',
  densityComfortable: 'Comfortable',
  autoPageSizeLabel: 'Auto ({rows})',
};

export const TanstackTableLocaleContext = createContext<TanstackTableLocale>(
  defaultTanstackTableLocale
);

export const useTanstackTableLocale = (): TanstackTableLocale =>
  useContext(TanstackTableLocaleContext);

interface TanstackTableLocaleProviderProps {
  /** Localized strings (partial overrides merge over the English defaults). */
  locale?: Partial<TanstackTableLocale>;
  children: ReactNode;
}

/**
 * Mount once near the app root to localize every `TanstackTable` underneath it,
 * so individual tables never need to pass `localeText`. Wire `locale` to your
 * app's i18n so there is a single source of truth for translations.
 *
 * ```tsx
 * <TanstackTableLocaleProvider locale={tableLocaleFromAppI18n}>
 *   <App />
 * </TanstackTableLocaleProvider>
 * ```
 */
export const TanstackTableLocaleProvider: FC<TanstackTableLocaleProviderProps> = ({
  locale,
  children,
}: TanstackTableLocaleProviderProps) => {
  const value = useMemo(() => ({ ...defaultTanstackTableLocale, ...locale }), [locale]);
  return (
    <TanstackTableLocaleContext.Provider value={value}>
      {children}
    </TanstackTableLocaleContext.Provider>
  );
};
